(() => {
  'use strict';

  /* Mobile menu */
  const initMenu = () => {
    const toggle = document.querySelector('[data-menu-toggle]');
    const nav = document.querySelector('[data-menu]');
    if (!toggle || !nav) return;

    const setOpen = (open) => {
      toggle.setAttribute('aria-expanded', String(open));
      nav.dataset.open = String(open);
      document.body.classList.toggle('menu-open', open);
    };

    toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'));
    nav.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && nav.dataset.open === 'true') {
        setOpen(false);
        toggle.focus();
      }
    });
  };

  /* Single reveal moment, used by the hero only */
  const initReveal = () => {
    const items = document.querySelectorAll('[data-reveal]');
    if (!items.length) return;
    requestAnimationFrame(() => {
      items.forEach((item, index) => {
        item.style.transitionDelay = `${index * 120}ms`;
        item.classList.add('is-visible');
      });
    });
  };

  /* Product gallery */
  const initGallery = (root) => {
    const main = root.querySelector('[data-gallery-main]');
    const thumbs = root.querySelectorAll('[data-gallery-thumb]');
    if (!main) return;

    const show = (thumb) => {
      main.src = thumb.dataset.src;
      main.srcset = thumb.dataset.srcset || '';
      main.alt = thumb.dataset.alt || '';
      thumbs.forEach((t) => t.removeAttribute('aria-current'));
      thumb.setAttribute('aria-current', 'true');
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => show(thumb)));
    return { showMedia: (mediaId) => {
      const match = root.querySelector(`[data-gallery-thumb][data-media-id="${mediaId}"]`);
      if (match) show(match);
    } };
  };

  /* Quantity steppers */
  const initQuantity = (root = document) => {
    root.querySelectorAll('[data-qty]').forEach((wrap) => {
      const input = wrap.querySelector('input');
      wrap.querySelectorAll('[data-qty-step]').forEach((button) => {
        button.addEventListener('click', () => {
          const step = Number(button.dataset.qtyStep);
          const min = Number(input.min || 0);
          const next = Math.max(min, (Number(input.value) || 0) + step);
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  };

  /* Variant picker: radio options mapped to the variant list */
  const initProduct = (root) => {
    const dataEl = root.querySelector('[data-product-json]');
    if (!dataEl) return;

    const product = JSON.parse(dataEl.textContent);
    const form = root.querySelector('.product-form');
    const idInput = form && form.querySelector('[name="id"]');
    const priceEl = root.querySelector('[data-price]');
    const skuEl = root.querySelector('[data-sku]');
    const button = root.querySelector('[data-add-button]');
    const gallery = initGallery(root);
    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    const labels = {
      add: button ? button.dataset.labelAdd : 'Add to cart',
      soldOut: button ? button.dataset.labelSoldOut : 'Sold out',
      unavailable: button ? button.dataset.labelUnavailable : 'Unavailable'
    };

    const formatMoney = (cents) => {
      const value = (cents / 100).toFixed(2);
      const [whole, decimals] = value.split('.');
      const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return moneyFormat
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${whole}.${decimals}`.replace('.', ','))
        .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${decimals}`);
    };

    const selected = () => Array.from(root.querySelectorAll('[data-option-index]')).map((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      return checked ? checked.value : null;
    });

    const update = () => {
      const options = selected();
      const variant = product.variants.find((v) => v.options.every((value, i) => value === options[i]));

      if (!variant) {
        if (button) {
          button.disabled = true;
          button.textContent = labels.unavailable;
        }
        return;
      }

      if (idInput) idInput.value = variant.id;

      if (priceEl) {
        let html = formatMoney(variant.price);
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          html += ` <s>${formatMoney(variant.compare_at_price)}</s>`;
        }
        priceEl.innerHTML = html;
      }

      if (skuEl) {
        skuEl.textContent = variant.sku || '—';
      }

      if (button) {
        button.disabled = !variant.available;
        button.textContent = variant.available ? labels.add : labels.soldOut;
      }

      if (gallery && variant.featured_media) {
        gallery.showMedia(variant.featured_media.id);
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    root.querySelectorAll('[data-option-index] input').forEach((input) => {
      input.addEventListener('change', update);
    });
  };

  /* Auto-submit sort select on collections */
  const initSort = () => {
    const select = document.querySelector('[data-sort]');
    if (!select) return;
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  };

  const init = () => {
    initMenu();
    initReveal();
    initQuantity();
    initSort();
    document.querySelectorAll('[data-product]').forEach(initProduct);
    document.querySelectorAll('[data-gallery]:not([data-product] [data-gallery])').forEach(initGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-initialise when sections change in the theme editor */
  document.addEventListener('shopify:section:load', () => {
    initReveal();
    initQuantity();
    document.querySelectorAll('[data-product]').forEach(initProduct);
  });
})();
